/**
 * Custom hook for fetching and managing simulation results.
 * @module useSimulationResults
 */

import { useState, useEffect, useCallback } from 'react';
import * as simulationResultsApi from '@/lib/simulationResultsApi';
import { mapApiSimulationToDomain } from '@/lib/mappers';
import { SimulationResult, ExamType, ExamLevel } from '@/types/simulation-results';
import { useAuth } from '@/hooks/useAuth';
import { shouldPollModuleAnalysis } from '@/lib/analysisStatus';

/**
 * Hook to fetch and manage simulation results with filtering.
 * 
 * @param level - Optional level filter ('all' or specific level)
 * @param examType - Optional exam type filter ('all' or specific type)
 */
export function useSimulationResults(
    level: ExamLevel | 'all' = 'all',
    examType: ExamType | 'all' = 'all'
) {
    const { user } = useAuth();
    const [results, setResults] = useState<SimulationResult[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchResults = useCallback(async (isSilent = false) => {
        if (!user?.id) return;

        if (!isSilent) {
            setIsLoading(true);
        }
        setError(null);

        try {
            let apiResults: simulationResultsApi.ApiSimulationSession[];

            if (level === 'all' || examType === 'all') {
                // Fetch all history if any filter is 'all'
                apiResults = await simulationResultsApi.getAllPreviousResults(user.id);
            } else {
                // Fetch filtered history
                apiResults = await simulationResultsApi.getPreviousResults(
                    user.id,
                    level as string,
                    examType as string
                );
            }

            if (apiResults) {
                let domainResults = apiResults.map(mapApiSimulationToDomain);

                // Perform local filtering if necessary (backend might return more results if a filter is 'all')
                if (level !== 'all') {
                    domainResults = domainResults.filter(r => r.level === level);
                }
                if (examType !== 'all') {
                    domainResults = domainResults.filter(r => r.examType.toLowerCase() === examType.toLowerCase());
                }

                setResults(domainResults);
            } else {
                setResults([]);
            }
        } catch (err) {
            console.error('Error fetching simulation results:', err);
            setError(err instanceof Error ? err : new Error('Unknown error occurred'));
        } finally {
            if (!isSilent) {
                setIsLoading(false);
            }
        }
    }, [user?.id, level, examType]);

    useEffect(() => {
        fetchResults(false); // Initial load is not silent
    }, [fetchResults]);

    const retryAnalysis = useCallback(async (sessionId: string, module: string) => {
        await simulationResultsApi.retryModuleAnalysis(sessionId, module);
        await fetchResults(true);
    }, [fetchResults]);

    // Poll while analysis is actively pending/running, but stop for terminal failures.
    useEffect(() => {
        const hasPendingAnalysis = results.some(r => r.modules.some(shouldPollModuleAnalysis));

        if (hasPendingAnalysis && !isLoading) {
            const intervalId = setInterval(() => {
                fetchResults(true); // Poll silently in the background
            }, 10000); // Poll every 10 seconds

            return () => clearInterval(intervalId);
        }
    }, [results, isLoading, fetchResults]);

    // Refetch when the user returns to the tab (e.g. after submitting a simulation elsewhere).
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchResults(true);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [fetchResults]);

    return {
        results,
        isLoading,
        error,
        refetch: fetchResults,
        retryAnalysis,
    };
}
